import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(email)}`);
      setSent(true);
      toast.success("Email envoyé si le compte existe");
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="bg-card border shadow-lg">
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="mx-auto">
              <img 
                src="/images/logo-betaye.jpg" 
                alt="Be-Taye" 
                className="w-28 h-auto mx-auto"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                Mot de passe oublié
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {sent ? "Vérifiez votre boîte mail" : "Entrez votre email pour réinitialiser"}
              </p>
            </div>
          </CardHeader>
          
          <CardContent>
            {sent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-foreground font-medium">Email envoyé !</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Si un compte existe avec l'adresse <strong>{email}</strong>, 
                    vous recevrez un lien de réinitialisation.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Le lien est valable pendant 1 heure.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setSent(false)}
                >
                  Renvoyer un email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm text-muted-foreground">
                    Adresse email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre.email@betaye.fr"
                      className="pl-10"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full btn-primary bg-primary text-primary-foreground font-semibold h-11"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></span>
                      Envoi en cours...
                    </span>
                  ) : "Envoyer le lien"}
                </Button>
              </form>
            )}
            
            <Link 
              to="/login" 
              className="flex items-center justify-center gap-2 mt-6 text-sm text-primary hover:underline"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à la connexion
            </Link>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
